import React, { useRef, useEffect } from 'react';
import './SpotlightLayout.css';

// Helper component to bind a MediaStream to a video element
const RemoteVideo = ({ stream, className }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [stream]);

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            className={className}
        />
    );
};

const SpotlightLayout = ({
    participants = [],
    currentUser,
    micOn,
    camOn,
    activeSpeakerId = null,
    selfVideoRef,
    selfVideoStyle,
    selfMicActive = false,
    onMicToggle,
    onCamToggle,
}) => {
    // Determine who is in the spotlight
    // Default to first participant if no active speaker, or current user if alone
    let spotlightUser = null;
    let otherUsers = [];

    if (activeSpeakerId) {
        spotlightUser = participants.find(p => p.id === activeSpeakerId);
        if (!spotlightUser && activeSpeakerId === 'me') {
            spotlightUser = { id: 'me', name: 'You', avatar: currentUser?.avatar, isSelf: true };
        }
    }

    // Fallback if no specific spotlight
    if (!spotlightUser) {
        if (participants.length > 0) {
            spotlightUser = participants[0];
            otherUsers = [...participants.slice(1), { id: 'me', name: 'You', avatar: currentUser?.avatar, isSelf: true }];
        } else {
            spotlightUser = { id: 'me', name: 'You', avatar: currentUser?.avatar, isSelf: true };
            otherUsers = [];
        }
    } else {
        // Filter out spotlight user from others list
        otherUsers = [
            ...participants.filter(p => p.id !== spotlightUser.id),
            ...(spotlightUser.id !== 'me' ? [{ id: 'me', name: 'You', avatar: currentUser?.avatar, isSelf: true }] : [])
        ];
    }

    const renderVideoTile = (user, isLarge = false) => (
        <div className={`spotlight-tile ${isLarge ? 'large' : 'small'} ${user.isSelf ? 'self' : ''} ${(user.id === activeSpeakerId || (user.isSelf && selfMicActive && micOn)) ? 'speaking' : ''}`}>
            <div className="spotlight-video-placeholder">
                {user.isSelf && camOn && selfVideoRef ? (
                    <video
                        ref={selfVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="spotlight-self-video"
                        style={selfVideoStyle}
                    />
                ) : user.stream ? (
                    <RemoteVideo stream={user.stream} className="spotlight-remote-video" />
                ) : (
                    <div className="spotlight-avatar">
                        {user.avatar || user.name?.[0] || '?'}
                    </div>
                )}
                {user.isSelf && (
                    <div className="spotlight-self-controls">
                        <button
                            className={`spotlight-control-btn ${micOn ? 'on' : 'off'}`}
                            onClick={onMicToggle}
                            title={micOn ? 'Mute' : 'Unmute'}
                        >
                            {micOn ? '🎤' : '🔇'}
                        </button>
                        <button
                            className={`spotlight-control-btn ${camOn ? 'on' : 'off'}`}
                            onClick={onCamToggle}
                            title={camOn ? 'Turn camera off' : 'Turn camera on'}
                        >
                            {camOn ? '📷' : '📷'}
                        </button>
                    </div>
                )}
            </div>
            <div className="spotlight-name-tag">
                {user.name} {user.muted || (user.isSelf && !micOn) ? '🔇' : ''}
            </div>
        </div>
    );

    return (
        <div className="spotlight-layout">
            <div className="spotlight-main-stage">
                {spotlightUser && renderVideoTile(spotlightUser, true)}
            </div>

            {otherUsers.length > 0 && (
                <div className="spotlight-strip">
                    {otherUsers.map(user => (
                        <div key={user.id} className="spotlight-strip-item">
                            {renderVideoTile(user, false)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SpotlightLayout;
